import React from 'react';
import { Box } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { AgronodTypography } from '@agronod/mui-components';

interface ChipSelectButtonProps {
  label: string;
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Default is "primary" (livsmedel-positive); "warning" is used for the
   * non-food exception chips so the two layers read visually distinct.
   */
  variant?: 'primary' | 'warning';
}

/**
 * ChipSelectButton — toggleable pill button for multi-select chip groups.
 *
 * Extracted from StepManualProduction's inline SelectableChip so the same
 * primitive can be reused across all farm-profile step components without
 * duplicating sx styling.
 *
 * @param label    Display text rendered inside the chip.
 * @param checked  Whether the chip is in the selected/active state.
 * @param onClick  Toggle handler — caller owns the state mutation.
 * @param disabled When true, the button is non-interactive (pointer-events off).
 * @param variant  Color accent: "primary" (default) or "warning".
 */
const ChipSelectButton: React.FC<ChipSelectButtonProps> = ({
  label,
  checked,
  onClick,
  disabled = false,
  variant = 'primary',
}) => {
  const accent = variant === 'warning' ? 'warning' : 'primary';

  return (
    <Box
      component="button"
      type="button"
      aria-pressed={checked}
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        border: '1.5px solid',
        borderColor: checked ? `${accent}.main` : 'grey.300',
        borderRadius: '100px',
        px: 1.75,
        py: 0.75,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: checked ? `${accent}.pastel` : 'background.paper',
        color: checked ? `${accent}.dark` : 'text.primary',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        '&:hover': {
          borderColor: disabled
            ? checked
              ? `${accent}.main`
              : 'grey.300'
            : checked
              ? `${accent}.dark`
              : `${accent}.main`,
          bgcolor: checked ? `${accent}.pastel` : 'background.paper',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: `${accent}.main`,
          outlineOffset: '2px',
        },
      }}
    >
      {checked && (
        <CheckRounded sx={{ fontSize: 16, color: `${accent}.dark` }} />
      )}
      <AgronodTypography
        variant="body2"
        sx={{
          fontWeight: checked ? 600 : 400,
          color: 'inherit',
        }}
      >
        {label}
      </AgronodTypography>
    </Box>
  );
};

export default ChipSelectButton;
